import type { SupabaseClient } from "@supabase/supabase-js"
import { isBlockedBarberName } from "@/lib/discovery-blocklist"
import { rankTalent } from "@/lib/talent/rank"
import type { BarberCard } from "@/components/network/talent-card"

// The talent roster owners source barbers from: the Hiring page's Talent feed
// and the owner Network page. Both used to carry their own copy of this fetch
// (one capped at 60 rows, which left everyone past the 60th unsearchable).
// One copy now, so the two lists hold the same people in the same order.

export type TalentFilters = {
  search: string
  city: string
  openToWorkOnly: boolean
  creatorOnly: boolean
  availability: string[]
  clientele: string[]
  payment: string[]
}

// Every published, visible barber. Search filters the fetched rows, so a
// barber past this cap would not merely be missing but unsearchable.
const ROSTER_LIMIT = 500

export async function fetchTalent(supabase: SupabaseClient, f: TalentFilters): Promise<BarberCard[]> {
  let query = supabase
    .from("barber_profiles")
    .select(
      "id, slug, city, state, years_experience, availability, clientele_size, payment_type_preference, is_open_to_work, is_content_creator, created_at, profiles(full_name, avatar_url)",
    )
    .eq("is_published", true)
    .eq("is_hidden", false)
    .order("created_at", { ascending: false })

  if (f.openToWorkOnly) query = query.eq("is_open_to_work", true)
  if (f.creatorOnly) query = query.eq("is_content_creator", true)
  if (f.availability.length > 0) query = query.overlaps("availability", f.availability)
  if (f.clientele.length > 0) query = query.overlaps("clientele_size", f.clientele)
  if (f.payment.length > 0) query = query.in("payment_type_preference", f.payment)
  if (f.city.trim()) query = query.ilike("city", `%${f.city.trim()}%`)

  const { data: rawData, error } = await query.limit(ROSTER_LIMIT)
  if (error) throw error
  if (!rawData) return []
  // Blocklisted test/junk accounts never reach the talent list.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = rawData.filter((b: any) => !isBlockedBarberName(b.profiles?.full_name))
  if (data.length === 0) return []

  // Portfolio previews, follower counts and activity scores for everyone in
  // three bulk calls, not per barber.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ids = data.map((b: any) => b.id as string)
  const [{ data: pics }, { data: follows }, activityRes] = await Promise.all([
    supabase
      .from("barber_portfolio_images")
      .select("barber_id, image_url, display_order, created_at")
      .in("barber_id", ids)
      .order("display_order", { ascending: true }),
    supabase.from("network_follows").select("following_id").in("following_id", ids),
    supabase.rpc("talent_activity", { barber_ids: ids }),
  ])

  // talent_activity() arrives with migration 1064. Until it is run (or if it
  // errors) rank on what the browser can see instead of failing the list.
  let activity: Map<string, number> | null = null
  if (!activityRes.error && Array.isArray(activityRes.data)) {
    activity = new Map(
      (activityRes.data as { barber_id: string; score: number }[]).map((r) => [r.barber_id, r.score]),
    )
  } else if (activityRes.error && activityRes.error.code !== "PGRST202") {
    console.warn("[talent] activity scores unavailable:", activityRes.error.message)
  }

  const picsByBarber = new Map<string, { image_url: string; created_at: string | null }[]>()
  for (const pic of pics || []) {
    const arr = picsByBarber.get(pic.barber_id) ?? []
    if (arr.length < 6) arr.push({ image_url: pic.image_url, created_at: pic.created_at ?? null })
    picsByBarber.set(pic.barber_id, arr)
  }
  const followerCount = new Map<string, number>()
  for (const fl of follows || []) followerCount.set(fl.following_id, (followerCount.get(fl.following_id) ?? 0) + 1)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const enriched = data.map((b: any) => ({
    ...b,
    portfolio: picsByBarber.get(b.id) ?? [],
    followers_count: followerCount.get(b.id) ?? 0,
  })) as BarberCard[]

  const q = f.search.trim().toLowerCase()
  const searched = q ? enriched.filter((b) => b.profiles?.full_name?.toLowerCase().includes(q)) : enriched

  return rankTalent(searched, activity)
}
